import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaBookmark, FaBookmark as FaBookmarkSolid, FaUndo } from 'react-icons/fa';
import { jobPostingService, JobPosting, JobType } from '../services/jobPostingService';
import LoadingSpinner from '../components/LoadingSpinner';
import { useMobile } from '../hooks/useMobile';
import CareersHeader from '../components/CareersHeader';
import FiltersSidebar from '../components/FiltersSidebar';
import FloatingFilterButton from '../components/FloatingFilterButton';
import { apiClient } from '../services/apiClient';

const SavedJobs: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useMobile();
  interface JobPostingWithContent extends JobPosting {
    jobDescription?: string; // Added when fetching content from jobDescriptionId
  }

  const [jobPostings, setJobPostings] = useState<JobPostingWithContent[]>([]);
  const [filteredPostings, setFilteredPostings] = useState<JobPostingWithContent[]>([]);
  const [savedJobs, setSavedJobs] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    jobTypes: [] as JobType[],
    positions: [] as string[],
  });

  // Applied filters (what's actually being used)
  const [appliedFilters, setAppliedFilters] = useState({
    jobTypes: [] as JobType[],
    positions: [] as string[],
  });


  // Load job postings and filter to show only saved ones
  useEffect(() => {
    loadJobPostings();
    // Load saved jobs from localStorage
    const loadSavedJobs = () => {
      const saved = localStorage.getItem('savedJobs');
      if (saved) {
        setSavedJobs(new Set(JSON.parse(saved)));
      }
    };
    loadSavedJobs();

    // Listen for storage changes (when jobs are saved from other tabs/pages)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'savedJobs') {
        loadSavedJobs();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom event (for same-tab updates)
    const handleCustomStorageChange = () => {
      loadSavedJobs();
    };
    window.addEventListener('savedJobsUpdated', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('savedJobsUpdated', handleCustomStorageChange);
    };
  }, []);

  // Apply filters when job postings, saved jobs, or applied filters change
  useEffect(() => {
    applyFilters();
  }, [jobPostings, savedJobs, appliedFilters]);

  const loadJobPostings = async () => {
    try {
      setLoading(true);
      const postings = await jobPostingService.getAllJobPostings();
      
      // Fetch job description content for each posting that has a jobDescriptionId
      const postingsWithContent = await Promise.all(
        postings.map(async (posting: JobPosting) => {
          if (posting.jobDescriptionId) {
            try {
              const contentResponse = await apiClient.get(`/job-descriptions/${posting.jobDescriptionId}/content`);
              return { ...posting, jobDescription: contentResponse.data?.content || '' };
            } catch (error: any) {
              console.error(`Error loading content for job posting ${posting.id}:`, error);
              return { ...posting, jobDescription: '' };
            }
          }
          return { ...posting, jobDescription: '' };
        })
      );
      
      setJobPostings(postingsWithContent);
    } catch (error: any) {
      console.error('Error loading job postings:', error);
      // Don't show errors for empty responses (404, empty array) - just set empty array
      // Only log actual server errors (500+) for debugging, but don't show toasts
      if (error.response?.status && error.response.status >= 500) {
        console.error('Server error loading job postings:', error?.response?.data || error?.message);
      }
      // Set empty array to show "No data" message - don't show error toasts
      setJobPostings([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    // First filter to show only saved jobs
    let filtered = jobPostings.filter(posting => savedJobs.has(posting.id));

    // Filter by job type
    if (appliedFilters.jobTypes.length > 0) {
      filtered = filtered.filter(posting => appliedFilters.jobTypes.includes(posting.jobType));
    }

    // Filter by position
    if (appliedFilters.positions.length > 0) {
      filtered = filtered.filter(posting => appliedFilters.positions.includes(posting.position));
    }

    setFilteredPostings(filtered);
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
  };

  const handleClearFilters = () => {
    setFilters({
      jobTypes: [],
      positions: [],
    });
    setAppliedFilters({
      jobTypes: [],
      positions: [],
    });
  };

  const handleJobTypeToggle = (jobType: JobType) => {
    setFilters(prev => ({
      ...prev,
      jobTypes: prev.jobTypes.includes(jobType)
        ? prev.jobTypes.filter(t => t !== jobType)
        : [...prev.jobTypes, jobType],
    }));
  };

  const handlePositionToggle = (position: string) => {
    setFilters(prev => ({
      ...prev,
      positions: prev.positions.includes(position)
        ? prev.positions.filter(p => p !== position)
        : [...prev.positions, position],
    }));
  };

  const handleSaveJob = (jobId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSavedJobs = new Set(savedJobs);
    if (newSavedJobs.has(jobId)) {
      newSavedJobs.delete(jobId);
    } else {
      newSavedJobs.add(jobId);
    }
    setSavedJobs(newSavedJobs);
    localStorage.setItem('savedJobs', JSON.stringify(Array.from(newSavedJobs)));
    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new Event('savedJobsUpdated'));
  };

  const handleReadMore = (posting: JobPosting) => {
    navigate(`/job/${posting.id}`);
  };

  const POSITION_OPTIONS = ['Prepper', 'Cleaner', 'Detailer', 'Delivery Driver', 'Tunnel Driver'];

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <CareersHeader />

      {/* Content with top padding to account for fixed header */}
      <div className="pt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className={`grid grid-cols-1 ${savedJobs.size > 0 ? 'lg:grid-cols-4' : ''} gap-8`}>
            {/* Left Sidebar - Filters (Desktop only, only show if there are saved jobs) */}
            {!isMobile && savedJobs.size > 0 && (
              <div className="lg:col-span-1">
                <FiltersSidebar
                  filters={filters}
                  onJobTypeToggle={handleJobTypeToggle}
                  onPositionToggle={handlePositionToggle}
                  onApplyFilters={handleApplyFilters}
                  onClearFilters={handleClearFilters}
                  positionOptions={POSITION_OPTIONS}
                />
              </div>
            )}

            {/* Right Section - Saved Jobs */}
            <div className={isMobile ? 'w-full' : (savedJobs.size > 0 ? 'lg:col-span-3' : 'lg:col-span-4')}>
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-gray-300">
                  <h2 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-gray-900`}>Saved Jobs</h2>
                  <span className={`text-gray-600 font-medium ${isMobile ? 'text-sm' : ''}`}>
                    {filteredPostings.length} {filteredPostings.length === 1 ? 'Result' : 'Results'}
                  </span>
                </div>

                {loading ? (
                  <div className="flex justify-center py-12">
                    <LoadingSpinner />
                  </div>
                ) : filteredPostings.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    {savedJobs.size === 0 
                      ? (
                        <>
                          <p className="text-lg mb-4">You haven't saved any jobs yet.</p>
                          <Link
                            to="/careers"
                            className="text-blue-600 hover:text-blue-700 underline"
                          >
                            Browse available jobs
                          </Link>
                        </>
                      )
                      : 'No saved jobs match the selected filters.'}
                  </div>
                ) : (
                  <div className="space-y-6">
                    {filteredPostings.map((posting) => (
                      <div
                        key={posting.id}
                        className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h3 className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-gray-900 mb-2`}>{posting.jobTitle}</h3>
                            {posting.jobId && (
                              <p className="text-sm text-gray-600 mb-1">Job ID: {posting.jobId}</p>
                            )}
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Job Type:</span> {posting.jobType}
                            </p>
                          </div>
                        </div>

                        <div className="mb-4">
                          <p className="text-gray-700 leading-relaxed">
                            {posting.jobDescription 
                              ? (posting.jobDescription.length > 200 
                                  ? posting.jobDescription.substring(0, 200).trim() + '...'
                                  : posting.jobDescription)
                              : 'Job description not available.'}
                          </p>
                        </div>

                        <div className={`flex ${isMobile ? 'flex-col space-y-2 -mx-6 px-6' : 'items-center justify-between'}`}>
                          <div className={`flex ${isMobile ? 'flex-col w-full space-y-2' : 'space-x-3'}`}>
                            <button
                              onClick={(e) => handleSaveJob(posting.id, e)}
                              className={`flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                                isMobile ? 'w-full' : ''
                              } ${
                                savedJobs.has(posting.id)
                                  ? 'bg-blue-600 text-white hover:bg-blue-700 text-sm'
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300 text-sm'
                              }`}
                            >
                              {savedJobs.has(posting.id) ? (
                                <>
                                  <FaBookmarkSolid className="w-4 h-4" />
                                  <span>Remove</span>
                                </>
                              ) : (
                                <>
                                  <FaBookmark className="w-4 h-4" />
                                  <span>Save</span>
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => handleReadMore(posting)}
                              className={`bg-blue-600 text-sm text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap ${
                                isMobile ? 'w-full' : ''
                              }`}
                            >
                              Read Full Job Description
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Filters Bottom Sheet */}
      {isMobile && savedJobs.size > 0 && mobileFiltersOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={() => setMobileFiltersOpen(false)}
          ></div>
          {/* Bottom Sheet */}
          <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-lg shadow-lg z-50 flex flex-col max-h-[80vh]">
            {/* Sticky Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-900">Filters</h2>
              <button
                onClick={() => setMobileFiltersOpen(false)}
                className="text-gray-600 hover:text-gray-800"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* Scrollable Content */}
            <div className="overflow-y-auto flex-1 relative">
              {/* Clear Filters - Sticky Above Job Type, Right Corner */}
              <div className="sticky top-0 bg-white z-10 px-4 pt-4 pb-2 flex justify-end">
                <button
                  onClick={() => {
                    handleClearFilters();
                    setMobileFiltersOpen(false);
                  }}
                  className="flex items-center space-x-1 text-gray-600 hover:text-gray-800 transition-colors relative group"
                >
                  <FaUndo className="w-2 h-2" />
                  <span className="text-xs">Clear filters</span>
                  <span className="absolute bottom-0 left-0 right-0 h-[1px] bg-gray-800 scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></span>
                </button>
              </div>
              <div className="p-4 pt-2">
                {/* Job Type Filter */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Job Type</h3>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.jobTypes.includes(JobType.FULL_TIME)}
                        onChange={() => handleJobTypeToggle(JobType.FULL_TIME)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Full-Time</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.jobTypes.includes(JobType.PART_TIME)}
                        onChange={() => handleJobTypeToggle(JobType.PART_TIME)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Part-Time</span>
                    </label>
                  </div>
                </div>

                {/* Position Filter */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Position</h3>
                  <div className="space-y-2">
                    {POSITION_OPTIONS.map((position) => (
                      <label key={position} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={filters.positions.includes(position)}
                          onChange={() => handlePositionToggle(position)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{position}</span>
                      </label>
                    ))}
                  </div>
                </div>

              </div>
              {/* Apply Button - Sticky at Bottom */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 mt-4">
                <button
                  onClick={() => {
                    handleApplyFilters();
                    setMobileFiltersOpen(false);
                  }}
                  className="w-full bg-blue-600 text-white px-4 py-3 rounded-md hover:bg-blue-700 transition-colors font-semibold"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Floating Filter Button - Mobile */}
      <FloatingFilterButton
        onClick={() => setMobileFiltersOpen(true)}
        show={isMobile && savedJobs.size > 0}
      />
    </div>
  );
};

export default SavedJobs;

